const steps = [
  {
    image: '/onboarding/mindstream1.png',
    title: 'Step 1: Open the Extension',
    description:
      'Click the Mindstream AI icon in your Chrome toolbar. The popup opens with a "Sign in with Google" button.',
    tip: 'If you don\'t see the icon, click the puzzle piece icon in the toolbar and pin Mindstream AI.',
  },
  {
    image: '/onboarding/mindstream2.png',
    title: 'Step 2: Choose Your Google Account',
    description:
      'A Google sign-in window appears. Select the Google account you want to use with Mindstream. Click the account once and wait — the window will close automatically.',
    tip: 'Important: Click your account only once. Clicking twice can interrupt the sign-in flow. The popup will close during sign-in — this is normal.',
  },
  {
    image: '/onboarding/mindstream3.png',
    title: 'Step 3: Grant Account Access',
    description:
      'Google asks you to confirm that Mindstream can access your name, email, and profile picture. Click "Continue" to proceed.',
  },
  {
    image: '/onboarding/mindstream4.png',
    title: 'Step 4: Authorize Gmail Access',
    description:
      'After signing in, reopen the extension popup. You\'ll see "Authorize Gmail Access". Click the button to grant Mindstream access to read your emails and prepare drafts.',
    tip: 'The popup closes during this step too. Reopen it after the Google window closes.',
  },
  {
    image: '/onboarding/mindstream5.png',
    title: 'Step 5: App Verification Notice',
    description:
      'You may see a "Google hasn\'t verified this app" screen. This is normal during our beta period. Click "Continue" to proceed.',
    tip: 'This screen appears because our Google OAuth verification is still in progress. It is safe to continue.',
  },
  {
    image: '/onboarding/mindstream6.png',
    title: 'Step 6: Confirm Gmail Permissions',
    description:
      'Google shows two permissions: viewing your email messages and managing drafts. Mindstream reads your emails to generate action items and can prepare reply drafts in your Gmail — drafts are never sent automatically; you always review and send them yourself. Click "Continue" to grant access.',
  },
  {
    image: '/onboarding/mindstream7.png',
    title: 'Step 7: Ready to Go!',
    description:
      'You\'re all set. Reopen the extension popup and click "Update actions" to scan your recent emails and generate prioritized action items.',
    tip: 'Action items are generated by AI from your recent emails. The first scan may take a few minutes.',
  },
];

export default function Onboarding() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-8 sm:px-6">
          <a href="/" className="text-sm text-indigo-600 hover:text-indigo-700 mb-4 inline-block">
            &larr; Back to Mindstream
          </a>
          <h1 className="text-3xl font-bold text-gray-900">Getting Started with Mindstream AI</h1>
          <p className="mt-2 text-lg text-gray-600">
            Follow these steps to set up the Chrome extension and start generating action items from your Gmail.
          </p>
        </div>
      </div>

      {/* Before you start */}
      <div className="max-w-3xl mx-auto px-4 pt-8 sm:px-6">
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-indigo-900 mb-2">Before you start</h2>
          <p className="text-indigo-800 leading-relaxed">
            Switch to the Chrome profile for the Gmail account you want Mindstream to read.
            Mindstream sign-in and Gmail access should both be completed with that same Google account.
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="max-w-3xl mx-auto px-4 py-10 sm:px-6 space-y-12">
        {steps.map((step, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Image */}
            <div className="bg-gray-100 flex items-center justify-center p-4">
              <img
                src={step.image}
                alt={step.title}
                className="max-h-[480px] w-auto rounded-lg shadow-md"
              />
            </div>

            {/* Text */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </span>
                <h2 className="text-xl font-semibold text-gray-900">{step.title}</h2>
              </div>
              <p className="text-gray-600 mt-2 leading-relaxed">{step.description}</p>
              {step.tip && (
                <div className="mt-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <span className="font-semibold">Tip:</span> {step.tip}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Footer */}
        <div className="text-center pb-10">
          <p className="text-gray-500 text-sm">
            Having trouble? Contact us at{' '}
            <a href="mailto:srmayecheng@gmail.com" className="text-indigo-600 hover:text-indigo-700">
              srmayecheng@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
